import { ChangeEvent, useState, useRef, type ReactNode } from "react";
import { isGeneratedAvatar } from "@/utils/brandLogo";
import { Upload, Image as ImageIcon } from "react-feather";
import toast from "react-hot-toast";

// Brand assets render on every affiliate/portal pageview, so only formats
// every browser rasterizes identically are allowed. This validates BOTH the
// file picker and the drag-and-drop path (which `accept` alone never covers).
const ACCEPTED_IMAGE_TYPES: Record<string, string> = {
  "image/png": "PNG",
  "image/jpeg": "JPEG",
  "image/webp": "WebP",
};
const MAX_IMAGE_SIZE_MB = 3;

interface ImageUploaderProps {
  image: string;
  onChange: (file: File) => void;
  size?: "sm" | "md" | "lg";
  shape?: "round" | "rectangular";
  id: string;
  layout?: "vertical" | "horizontal";
  helpText?: string;
  /** When set (e.g. 2 for 2:1), the preview is constrained to this aspect ratio. Use for rectangular previews. */
  previewAspectRatio?: number;
  /**
   * Shown in the preview slot when there is no real image (empty, our
   * placeholder, or a generated letter avatar) instead of the generic icon —
   * e.g. brand-coloured initials.
   */
  fallback?: ReactNode;
  /** How a rectangular preview fits its box: crop to fill (default) or letterbox. */
  previewFit?: "cover" | "contain";
  /**
   * Off when the page shows the image somewhere else (a live preview card),
   * so only the drop zone renders and nothing is shown twice.
   */
  showPreview?: boolean;
}

export function ImageUploader({
  image,
  onChange,
  size = "md",
  shape = "round",
  id,
  layout = "vertical",
  helpText,
  previewAspectRatio,
  fallback,
  previewFit = "cover",
  showPreview = true,
}: ImageUploaderProps) {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const sizeClasses = {
    sm: "h-24 w-24",
    md: "h-32 w-32",
    lg: shape === "rectangular" ? "h-32 w-full" : "h-40 w-40",
  };

  const useAspectRatio = shape === "rectangular" && previewAspectRatio != null && previewAspectRatio > 0;
  const isPlaceholder =
    !image || image.includes("/placeholder/no-image") || (!!fallback && isGeneratedAvatar(image));

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file: File) => {
    // Reset input value to allow uploading the same file again
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    if (!ACCEPTED_IMAGE_TYPES[file.type]) {
      toast.error(
        `Unsupported file type. Please upload a ${Object.values(
          ACCEPTED_IMAGE_TYPES
        ).join(", ")} image.`
      );
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
      toast.error(`Image is too large. Maximum size is ${MAX_IMAGE_SIZE_MB}MB.`);
      return;
    }
    onChange(file);
  };

  return (
    <div className={`w-full ${layout === "horizontal" ? "flex items-center gap-8" : ""}`}>
      {/* Image Preview */}
      {showPreview && (image || fallback) && (
        <div
          className={`${layout === "vertical" ? "mb-4" : ""} ${shape === "rectangular" ? "w-full" : "w-fit"} ${useAspectRatio ? "overflow-hidden" : ""}`}
          style={useAspectRatio ? { aspectRatio: `${previewAspectRatio}/1` } : undefined}
        >
          {isPlaceholder && fallback ? (
            <div
              className={`overflow-hidden ${shape === "round" ? "rounded-full" : "rounded-lg"} ${
                useAspectRatio ? "w-full h-full" : sizeClasses[size]
              }`}
            >
              {fallback}
            </div>
          ) : isPlaceholder ? (
            <div
              className={`flex items-center justify-center border border-border bg-muted text-muted-foreground ${
                shape === "round" ? "rounded-full" : "rounded-lg"
              } ${useAspectRatio ? "w-full h-full" : sizeClasses[size]}`}
            >
              <ImageIcon className="h-1/3 w-1/3 opacity-50" />
            </div>
          ) : (
            <img
              src={image}
              alt="Uploaded"
              className={`bg-muted ${
                shape === "round" ? "rounded-full" : "rounded-lg"
              } ${previewFit === "contain" ? "object-contain" : "object-cover"} ${
                useAspectRatio ? "w-full h-full" : sizeClasses[size]
              }`}
            />
          )}
        </div>
      )}

      {/* Upload Interface */}
      <div
        className={`border-2 border-dashed border-border rounded-lg p-6 text-center transition-colors ${
          dragActive ? "border-brand-500 bg-brand-50" : ""
        } ${layout === "horizontal" ? "flex-1" : ""}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center gap-2">
          <Upload className="h-6 w-6 text-muted-foreground" />
          <div className="text-sm text-muted-foreground">
            <input
              ref={inputRef}
              type="file"
              onChange={handleChange}
              accept={Object.keys(ACCEPTED_IMAGE_TYPES).join(",")}
              className="hidden"
              id={id}
            />
            <label
              htmlFor={id}
              className="cursor-pointer text-brand-600 hover:text-brand-700"
            >
              Click to upload
            </label>
            <span className="mx-1">or drag and drop</span>
          </div>
          {helpText && (
            <p className="text-xs text-muted-foreground">
              {helpText}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
